import { useEffect, useRef, useState } from 'react'
import { analyticsService } from '@meiuca/analytics'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents'
import { Button, ButtonVariant, Body, Caption } from '@meiuca/ui-components'
import { formatCurrency } from '@meiuca/web-foundation'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { toastManager } from '@/lib/toastManager'
import { PixPayment } from '@/components/PixPayment'
import {
  classPackageService,
  type ClassPackageForSale,
  type ClassPackagePurchaseDetail
} from '@/services/classPackageService'

const PURCHASE_POLL_INTERVAL_MS = 4000

const PURCHASE_ERROR_MESSAGES: Record<string, string> = {
  class_package_not_found: 'Este pacote não está mais à venda. Recarregue e tente de novo.',
  class_package_inactive: 'Este pacote não está mais à venda. Recarregue e tente de novo.',
  pix_not_available: 'Este centro ainda não aceita pagamento por Pix pelo app.',
  payer_missing_document: 'Seu cadastro não tem CPF. Fale com o centro para completar seus dados.',
  payer_missing_phone: 'Seu cadastro não tem telefone. Fale com o centro para completar seus dados.',
  training_center_customer_not_found: 'Você não é aluno deste centro de treinamento.'
}

type FlowState =
  | { kind: 'catalog' }
  | { kind: 'pix'; purchaseId: string; priceCents: number; detail: ClassPackagePurchaseDetail | null }
  | { kind: 'succeeded'; classCount: number }
  | { kind: 'expired'; packageId: string }

export interface ClassPackagePurchaseFlowProps {
  trainingCenterId: string
  packages: ClassPackageForSale[]
  /** Analytics only: the class the student was trying to book, when any. */
  occurrenceId?: string
  /** Analytics: where the flow started from. */
  source: 'checkout' | 'credits' | 'showcase'
  /**
   * Fired when the user acknowledges the confirmed purchase — the caller
   * typically resumes its own flow (e.g. the class checkout reloads its
   * options, now finding credits). Falls back to `onClose` when absent.
   */
  onCompleted?: () => void
  onClose: () => void
}

/**
 * The package checkout: catalog → Pix → credits on the account. Purchasing is
 * all it does — callers that came from a class resume their own enrollment
 * after `onCompleted`. Shared by the self-enroll checkout and the credits
 * screen ("Comprar mais créditos"); rendered inside a Modal owned by the
 * caller.
 */
export function ClassPackagePurchaseFlow({
  trainingCenterId,
  packages,
  occurrenceId,
  source,
  onCompleted,
  onClose
}: ClassPackagePurchaseFlowProps) {
  const [state, setState] = useState<FlowState>({ kind: 'catalog' })
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    packages.length === 1 ? packages[0].id : null
  )
  const [isStarting, setIsStarting] = useState(false)
  const offeredTracked = useRef(false)

  const baseParams = {
    ct_id: trainingCenterId,
    source,
    ...(occurrenceId ? { occurrence_id: occurrenceId } : {})
  }

  useEffect(() => {
    if (offeredTracked.current) return
    offeredTracked.current = true
    analyticsService.track(ANALYTICS_EVENTS.CLASS_PACKAGE_OFFERED, {
      ...baseParams,
      packages_offered: packages.length
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const startPurchase = async (packageId: string) => {
    const pkg = packages.find((item) => item.id === packageId)
    if (!pkg || isStarting) return

    try {
      setIsStarting(true)
      analyticsService.track(ANALYTICS_EVENTS.CLASS_PACKAGE_PURCHASE_STARTED, {
        ...baseParams,
        package_id: pkg.id,
        price_cents: pkg.priceCents,
        class_count: pkg.classCount
      })
      const purchase = await classPackageService.purchase(trainingCenterId, packageId)
      setState({ kind: 'pix', purchaseId: purchase.id, priceCents: purchase.priceCents, detail: null })
    } catch (err) {
      console.error('Error starting class package purchase:', err)
      const code = (err as { code?: string })?.code
      toastManager.error(
        (code && PURCHASE_ERROR_MESSAGES[code]) ??
          'Não foi possível iniciar a compra. Tente novamente.'
      )
    } finally {
      setIsStarting(false)
    }
  }

  // Polls the purchase while it waits for the Pix / the payment webhook.
  const pollingPurchaseId = state.kind === 'pix' ? state.purchaseId : null
  useEffect(() => {
    if (!pollingPurchaseId) return
    let cancelled = false
    let hadPix = false

    const poll = async () => {
      try {
        const detail = await classPackageService.getPurchase(pollingPurchaseId)
        if (cancelled) return

        if (!hadPix && detail.pix?.payload) {
          hadPix = true
          analyticsService.track(ANALYTICS_EVENTS.CLASS_PACKAGE_PIX_DISPLAYED, baseParams)
        }

        if (detail.purchase.status === 'active') {
          analyticsService.track(ANALYTICS_EVENTS.CLASS_PACKAGE_PURCHASE_CONFIRMED, {
            ...baseParams,
            price_cents: detail.purchase.priceCents,
            class_count: detail.purchase.classCount
          })
          setState({ kind: 'succeeded', classCount: detail.purchase.classCount })
          return
        }

        if (detail.purchase.status === 'canceled') {
          analyticsService.track(ANALYTICS_EVENTS.CLASS_PACKAGE_PURCHASE_EXPIRED, baseParams)
          setState((current) =>
            current.kind === 'pix'
              ? { kind: 'expired', packageId: selectedPackageId ?? packages[0]?.id }
              : current
          )
          return
        }

        setState((current) => (current.kind === 'pix' ? { ...current, detail } : current))
      } catch (err) {
        // Transient polling error: keep the current screen, try again next tick.
        console.error('Error polling class package purchase:', err)
      }
    }

    poll()
    const interval = setInterval(poll, PURCHASE_POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingPurchaseId])

  if (state.kind === 'succeeded') {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="w-5 h-5 text-feedback-success flex-shrink-0 mt-0.5" />
          <Body className="text-text-primary">
            Pagamento confirmado! Seus{' '}
            {state.classCount === 1 ? 'crédito já está' : `${state.classCount} créditos já estão`} na
            sua conta.
          </Body>
        </div>
        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={() => (onCompleted ?? onClose)()}
          className="w-full"
        >
          Continuar
        </Button>
      </div>
    )
  }

  if (state.kind === 'expired') {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-feedback-error flex-shrink-0 mt-0.5" />
          <Body className="text-text-primary">
            O Pix expirou e a compra foi cancelada. Nada foi cobrado — se ainda quiser os créditos,
            comece de novo.
          </Body>
        </div>
        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={() => startPurchase(state.packageId)}
          disabled={isStarting}
          className="w-full"
        >
          {isStarting ? 'Tentando de novo...' : 'Tentar de novo'}
        </Button>
        <Button variant={ButtonVariant.SECONDARY} onClick={onClose} className="w-full">
          Fechar
        </Button>
      </div>
    )
  }

  if (state.kind === 'pix') {
    const pix = state.detail?.pix
    return (
      <div className="space-y-4">
        <Body className="text-text-primary">
          Pague <span className="font-semibold">{formatCurrency(state.priceCents)}</span> via Pix
          para receber seus créditos.
        </Body>
        {pix?.payload && pix.encodedImage ? (
          <>
            <PixPayment payload={pix.payload} qrCodeBase64={pix.encodedImage} />
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 text-text-tertiary animate-spin" />
              <Caption className="text-text-tertiary">Aguardando o pagamento...</Caption>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
            <Body className="text-text-secondary mt-3">Gerando o Pix...</Body>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {packages.map((pkg) => {
          const isSelected = selectedPackageId === pkg.id
          return (
            <button
              key={pkg.id}
              type="button"
              onClick={() => setSelectedPackageId(pkg.id)}
              disabled={isStarting}
              className={`w-full rounded-lg border p-3 text-left transition-colors ${
                isSelected
                  ? 'border-brand-primary bg-brand-primary/5'
                  : 'border-border-primary hover:border-border-secondary'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <Body className="font-medium text-text-primary">{pkg.name}</Body>
                  <Caption className="text-text-secondary">
                    {pkg.classCount} {pkg.classCount === 1 ? 'aula' : 'aulas'} · validade de{' '}
                    {pkg.validityDays} dias
                  </Caption>
                </div>
                <Body className="font-semibold text-text-primary flex-shrink-0">
                  {formatCurrency(pkg.priceCents)}
                </Body>
              </div>
            </button>
          )
        })}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant={ButtonVariant.SECONDARY}
          onClick={onClose}
          disabled={isStarting}
          className="w-full sm:flex-1"
        >
          Voltar
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={() => selectedPackageId && startPurchase(selectedPackageId)}
          disabled={!selectedPackageId || isStarting}
          className="w-full sm:flex-1"
        >
          {isStarting ? 'Iniciando...' : 'Comprar com Pix'}
        </Button>
      </div>
    </div>
  )
}
